import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import ComicCard from '@/components/feature/ComicCard';
import { hotComics, newComics, categories } from '@/mocks/comics';
import { useEffect, useState } from 'react';

function HomePage() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '韩漫漫画网',
    url: 'https://hanman.com',
    description: '韩漫漫画网是热门漫画在线阅读平台，实现全网韩漫最快更新，涵盖韩漫大全，内容多样，加载快捷，为您提供极速阅读服务。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://hanman.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
    inLanguage: 'zh-CN',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫漫画网',
    url: 'https://hanman.com',
    description: '热门韩漫在线阅读平台，提供全网最快更新的韩漫资源',
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-9999',
      contactType: 'customer service',
      availableLanguage: 'Chinese',
    },
  };

  return (
    <>
      <SEOHead
        title="韩漫漫画网-热门漫画在线阅读平台-全网韩漫最快更新-韩漫大全"
        description="韩漫漫画网是热门漫画在线阅读平台，实现全网韩漫最快更新，涵盖韩漫大全，内容多样，加载快捷，为您提供极速阅读服务。"
        keywords="韩漫,热门漫画,在线阅读,最快更新,韩漫大全"
        canonicalUrl="/"
        schema={{ ...homeSchema, ...orgSchema }}
      />

      <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20manhwa%20digital%20art%20collage%2C%20colorful%20comic%20panel%20background%2C%20warm%20vibrant%20illustration%20style%2C%20abstract%20artistic%20composition%20with%20soft%20gradients%2C%20modern%20digital%20painting%2C%20orange%20amber%20rose%20warm%20tones%2C%20artistic%20texture%2C%20clean%20composition&width=1920&height=800&seq=home-hero-bg-01&orientation=landscape"
          alt="韩漫漫画网 - 热门漫画在线阅读平台"
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className={`text-center px-4 w-full transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              韩漫漫画网
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-6">
              热门漫画在线阅读平台，全网韩漫最快更新，涵盖韩漫大全<br />
              内容多样，加载快捷，为您提供极速阅读服务
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-book-open-line"></i>
                开始阅读
              </Link>
              <Link
                to="/category/romance"
                className="inline-flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm px-6 py-3 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-heart-line"></i>
                恋爱专题
              </Link>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">48+</div>
                <div className="text-xs text-white/60 mt-1">热门韩漫</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">100万+</div>
                <div className="text-xs text-white/60 mt-1">忠实读者</div>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-bold text-white">每日更新</div>
                <div className="text-xs text-white/60 mt-1">从不间断</div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-s-line text-white/60 text-2xl"></i>
        </div>
      </section>

      <main className="w-full bg-background-50">
        <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-labelledby="categories-title">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 id="categories-title" className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                漫画分类
              </h2>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/category/all' : `/category/${cat.slug}`}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 hover:bg-primary-100/70 transition-colors cursor-pointer group"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10 group-hover:bg-primary-500/20 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-500`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-950 whitespace-nowrap">{cat.name}</span>
                  <span className="text-xs text-foreground-500">{cat.count}部</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-labelledby="hot-title">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 id="hot-title" className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  热门推荐
                </h2>
                <span className="px-2.5 py-0.5 rounded-full bg-primary-500/10 text-primary-500 text-xs font-medium">
                  <i className="ri-fire-line mr-1"></i>HOT
                </span>
              </div>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                更多推荐 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-labelledby="new-title">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 id="new-title" className="text-xl md:text-2xl font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  最新更新
                </h2>
                <span className="px-2.5 py-0.5 rounded-full bg-accent-500/10 text-accent-500 text-xs font-medium">
                  <i className="ri-time-line mr-1"></i>NEW
                </span>
              </div>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1">
                更多更新 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop>
              {newComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100" aria-labelledby="features-title">
          <div className="max-w-7xl mx-auto">
            <h2 id="features-title" className="text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              为什么选择韩漫漫画网？
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-flashlight-line', title: '极速加载', desc: '采用先进的图片压缩与预加载技术，翻页几乎零延迟，沉浸式阅读体验。' },
                { icon: 'ri-smartphone-line', title: '多端适配', desc: '完美适配电脑、平板、手机等多种设备，随时随地畅享韩漫。' },
                { icon: 'ri-database-2-line', title: '海量资源', desc: '收录上千部热门韩漫，涵盖恋爱、奇幻、动作等多种题材。' },
                { icon: 'ri-cloud-line', title: '最快更新', desc: '与韩国同步更新，第一时间带来最新话，拒绝等待。' },
              ].map((feature, idx) => (
                <div key={idx} className="p-5 rounded-xl bg-background-50 text-center">
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500/10 mb-3">
                    <i className={`${feature.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-950 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-600">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14" aria-labelledby="cta-title">
          <div className="max-w-7xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Korean%20webtoon%20manhwa%20reading%20atmosphere%2C%20cozy%20warm%20lighting%2C%20digital%20comic%20reading%20on%20tablet%2C%20soft%20ambient%20light%2C%20warm%20amber%20and%20rose%20tones%2C%20comfortable%20home%20setting%2C%20artistic%20blur%20background%2C%20editorial%20style&width=1400&height=400&seq=home-cta-bg-01&orientation=landscape"
                alt="随时随地畅享韩漫阅读"
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-black/30"></div>
              <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between p-8 md:p-12 gap-6">
                <div className="text-center lg:text-left">
                  <h2 id="cta-title" className="text-2xl md:text-3xl font-bold text-white mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                    准备好开始你的韩漫之旅了吗？
                  </h2>
                  <p className="text-white/80 text-sm md:text-base">加入百万读者，享受极速阅读体验</p>
                </div>
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 bg-white text-foreground-950 hover:bg-background-100 px-8 py-3.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-book-open-line"></i>
                  立即开始阅读
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}

export default HomePage;